<template>
  <div>
    <div class="chat-header">
      <div class="pa-1">
        <v-btn
          color="blue"
          text
          class="back-button"
          elevation="0"
          @click="onClose"
          style="padding: 0;"
        >
          <v-icon>mdi-chevron-left</v-icon>
          Back
        </v-btn>
      </div>
      <div class="conversation-header" v-if="conversation">
        <div style="text-align: left; width: 100%;">
          <div v-if="!showRenameChat">
            <b>{{ conversation.friendlyName }}</b
            ><v-icon
              @click="showRenameChat = true"
              class="pa-1 mb-1"
              size="16px"
              >mdi-pencil</v-icon
            >
          </div>
          <div v-else-if="showRenameChat">
            <v-text-field
              v-model="renameChatValue"
              @keydown.enter="handleChatRename"
              @keydown.esc="showRenameChat = false"
              dense
            >
              <template v-slot:append>
                <v-icon color="green" class="pa-1" @click="handleChatRename"
                  >mdi-check</v-icon
                >
                <v-icon color="red" class="pa-1" @click="showRenameChat = false"
                  >mdi-close</v-icon
                >
              </template>
            </v-text-field>
          </div>
          <div class="hidden-sm-and-down">
            <ChatMember
              v-for="participant in visibleParticipants"
              :key="participant.sid"
              :participant="participant"
            />
            <span
              class="text-decoration-underline blue--text pointer ml-2"
              v-if="hiddenParticipants.length > 0 && !showAllParticipants"
              @click="showAllParticipants = true"
              >{{ hiddenParticipants.length }} More...
            </span>
            <span
              class="text-decoration-underline blue--text pointer ml-2"
              v-if="visibleParticipants.length > 10 && showAllParticipants"
              @click="showAllParticipants = false"
              >Show Less
            </span>
          </div>

          <div class="hidden-md-and-up">
            {{ this.participants.length }} Participants
            <div v-if="showAllParticipants">
              <ChatMember
                v-for="participant in visibleParticipants"
                :key="participant.sid"
                :participant="participant"
              />
            </div>
            <span
              class="text-decoration-underline blue--text pointer ml-2"
              v-if="!showAllParticipants"
              @click="showAllParticipants = true"
              >Show
            </span>
            <span
              class="text-decoration-underline blue--text pointer ml-2"
              v-if="showAllParticipants"
              @click="showAllParticipants = false"
              >Hide
            </span>
          </div>
        </div>
      </div>
    </div>
    <div class="chat-body">
      <div class="conversation" v-if="conversation">
        <div class="messages-layout">
          <div id="messages-scroll" class="messages-container">
            <div class="messages pa-2">
              <div>
                <v-btn
                  v-if="canLoadMore && !loading"
                  color="blue"
                  block
                  text
                  @click="loadMoreMessages"
                >
                  Load More
                </v-btn>
                <v-btn
                  v-if="loading"
                  color="blue"
                  block
                  text
                  disabled
                  @click="() => {}"
                >
                  <v-icon>mdi-cached</v-icon> Loading
                </v-btn>
              </div>
              <ChatMessage
                v-for="(item, index) in messages"
                ref="messages"
                :id="'message-' + item.sid"
                :key="item.sid"
                :language="language"
                :message="item"
                :user="user"
                @showContextMenu="handleMessageShowContextMenu"
                :chatClient="chatClient"
                :previousMessage="index > 0 ? messages[index - 1] : null"
                :nextMessage="
                  index < messages.length - 2 ? messages[index + 1] : null
                "
                :onImageClick="handleImageClick"
              />
              <v-menu
                v-model="showReactionMenu"
                ref="reactionMenu"
                absolute
                :close-delay="5"
                :close-on-click="false"
                :close-on-content-click="false"
                offset-y
                :position-x="reactMenuPosition.left"
                :position-y="reactMenuPosition.top"
              >
                <ReactionSelector
                  @react="handleReact"
                  :reactActionLabel="reactActionLabel"
                  :onAction="handleReact"
                />
              </v-menu>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="chat-footer">
      <div class="conversation-message-form">
        <div v-if="!chatEnabled" style="text-align:center;">
          <v-chip small class="tb-red-bg ma-2" style="margin: 0 auto;">
            Chat is disabled for this team.
          </v-chip>
        </div>
        <div class="messages-layout">
          <div class="send-message-file-input">
            <div v-if="this.file && this.dataUrl">
              <v-img
                v-if="isImageAttachment"
                max-height="96"
                max-width="140"
                class="pa-2"
                :src="dataUrl"
                style="border-radius: 5px; margin-top: -100px; margin-left: -2px; border: 1px solid rgba(255,255,255,.5);"
              >
                <div class="black-icon-background" />
                <v-icon @click="file = null" color="white" class="image-delete"
                  >mdi-close-circle
                </v-icon>
              </v-img>

              <div
                v-if="!isImageAttachment"
                class="file-attachment"
                @click="file = null"
              >
                <div class="black-icon-background" />
                <v-icon @click="file = null" color="white" class="image-delete"
                  >mdi-close-circle</v-icon
                >
                <v-icon>mdi-file-document</v-icon>
                <span>{{ friendlyFileName }}</span>
              </div>
            </div>
          </div>

          <div v-if="chatEnabled" class="send-message">
            <v-file-input
              v-model="file"
              prepend-icon="mdi-camera"
              filled
              hide-input
              label="File input"
              style="float: left;"
              @change="handleFileChange($event)"
            />
            <v-textarea
              @click:append="send"
              @keydown="startTyping"
              @keydown.enter.prevent="handleEnter"
              append-icon="mdi-send"
              class="message-input"
              auto-grow
              rows="1"
              label="Type a message"
              hide-details
              outlined
              v-model="message"
            />
          </div>
        </div>
      </div>
    </div>
    <v-dialog
      v-model="showMediaMessage"
      scrollable
      overlay-opacity="1"
      overlay-color="rgb(16, 25, 35)"
      width="1024"
      height="768"
    >
      <v-card color="transparent">
        <v-btn
          color="primary"
          @click="
            () => {
              this.showMediaMessage = false;
              this.mediaMessage = null;
            }
          "
          style="top: 50px; right: 50px; position: fixed;"
        >
          Close
        </v-btn>
        <div>
          <div v-if="showZoomImage">
            <img v-bind:src="zoomMediaUrl" width="100%" />
          </div>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import Vue from "vue";
import ChatMessage from "@/components/Chat/ChatMessage.vue";
import axios from "axios";
import { createTempMessage, videoType } from "../../helpers/chat";
import { isChatEnabled, react, updateChannelFriendlyName } from "@/helpers/api";
import { getImageDimensions, readFileAsDataURL } from "@/helpers/util";
import { mediaUrl, isImage, isVideo } from "@/helpers/chat";
import ChatMember from "@/components/Chat/ChatMember";
import ReactionSelector from "@/components/Chat/ReactionSelector";
import { bus } from "@/main";

export default Vue.extend({
  name: "ChatConversation",
  components: { ReactionSelector, ChatMember, ChatMessage },
  props: [
    "conversation",
    "chatClient",
    "user",
    "language",
    "onClose",
    "limitedFunctionality"
  ],
  data() {
    return {
      message: "",
      messageVisible: false,
      messages: [],
      canLoadMore: false,
      loading: true,
      typing: [],
      participants: [],
      users: [],
      file: null,
      dataUrl: null,
      chatEnabled: true,
      mediaMessage: null,
      showMediaMessage: false,
      showRenameChat: false,
      renameChatValue: null,
      showAllParticipants: false,
      reactionContext: null,
      showReactionMenu: false
    };
  },
  async mounted() {
    this.participants = await this.conversation.getParticipants();
    this.users = await this.participants.map(p => p.getUser());

    this.renameChatValue = this.conversation.friendlyName;

    const conv = this.conversation;
    this.conversation.on("messageAdded", this.messageAdded);
    this.conversation.on("messageUpdated", this.messageUpdated);
    this.conversation.on("messageRemoved", this.messageRemoved);
    this.conversation.on("typingStarted", this.typingStarted);
    this.conversation.on("typingEnded", this.typingEnded);

    this.loadMessages();

    const org = this.conversation.attributes.organizationId;
    const chatEnabled = await isChatEnabled(org);
    this.chatEnabled = true; // chatEnabled.data;
  },
  beforeDestroy() {
    console.log("ChatConversation: REMOVING conversation event listeners");
    this.conversation.off("messageAdded", this.messageAdded);
    this.conversation.off("messageUpdated", this.messageUpdated);
    this.conversation.off("messageRemoved", this.messageRemoved);
    this.conversation.off("typingStarted", this.typingStarted);
    this.conversation.off("typingEnded", this.typingEnded);
  },
  methods: {
    handleMessageShowContextMenu(event, message) {
      this.showReactionMenu = false;
      const context = { event, message };
      this.$nextTick(() => {
        this.reactionContext = context;
        this.showReactionMenu = true;
      });
    },

    handleReact(reaction) {
      if (this.reactionContext) {
        react(this.reactionContext.message, reaction.token)
          .then(() => {
            this.$nextTick(() => {
              this.reactionContext = null;
              this.showReactionMenu = false;
            });
          })
          .catch(err => {
            console.error(err);
          });
      }
    },

    scrollToBottom() {
      setTimeout(() => {
        const container = document.getElementById("messages-scroll");
        container.scrollTop = container.scrollHeight;
      }, 500);
    },

    scrollTo(message) {
      setTimeout(() => {
        const container = document.getElementById("messages-scroll");
        const messageElement = document.getElementById(
          "message-" + message.sid
        );
        container.scrollTop = messageElement.offsetTop - 150;
      }, 500);
    },

    handleImageClick(message) {
      this.mediaMessage = message;
      this.showMediaMessage = true;
    },

    loadMoreMessages() {
      this.loadMessages(true);
    },

    /**
     * Load the Messages for the current Channel
     */
    loadMessages(loadMore = false) {
      // console.log(`enter loadMessages[nextPage=${loadMore}]... ${this.conversation}`);
      this.loading = true;

      const messagesCopy = loadMore ? [...this.messages] : [];
      let previousMessage = messagesCopy.length ? messagesCopy[0] : null;

      const scrollToMessage = previousMessage;

      // what index do we load from?
      const loadFromIndex =
        loadMore && previousMessage
          ? previousMessage.index - 1
          : this.conversation.lastMessage
          ? this.conversation.lastMessage.index
          : 0;

      // are we at the end of messages?  if so, return
      if (loadFromIndex < 0) {
        this.loading = false;
        return;
      }

      this.conversation
        .getMessages(30, loadFromIndex, "backwards")
        .then(paginator => {
          this.canLoadMore = paginator.hasPrevPage;
          paginator.items.forEach((thisMessage, index, items) => {
            thisMessage.previousMessage = previousMessage;

            if (index < items.length - 1) {
              thisMessage.nextMessage = items[index + 1];
            }

            // add message
            messagesCopy.unshift(thisMessage);

            // store as prevMessage for later
            previousMessage = thisMessage;
          });

          this.messages = messagesCopy.sort((a, b) => a.index - b.index);

          if (loadMore) {
            this.scrollTo(scrollToMessage);
          } else {
            this.scrollToBottom();
          }

          if (!loadMore) {
            this.advanceLastConsumedMessageIndex(
              this.messages[this.messages.length - 1]
            );
          }
        })
        .catch(err => {
          this.error = err;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    /**
     * Update the user's lastConsumedMessageIndex to the most recent message we have loaded
     */
    advanceLastConsumedMessageIndex(message) {
      if (!message) {
        message = this.messages[this.messages.length - 1];
      }

      if (!message) return;

      // let Twilio know this user has seen this message, if applicable
      if (message.index > this.conversation.lastConsumedMessageIndex) {
        console.log("this.conversation.advanceLastConsumedMessageIndex");
        this.conversation.advanceLastConsumedMessageIndex(message.index);
      }
    },

    translate(message) {
      const url =
        "/rest-api/chat/translate-message/" +
        message.conversation.sid +
        "/" +
        message.sid +
        "/" +
        this.language;
      axios.get(url); // no need to get response, Twilio should file update event
    },

    supportedFileType() {
      const type = this.file && this.file.type;
      const supported =
        type !== null &&
        (type === "image/jpg" ||
        type === "image/jpeg" ||
        type === "image/gif" ||
        type === "image/png" ||
        type === "video/mpeg" ||
        type === "video/mp4" ||
        type === "video/x-msvideo" ||
        type === "video/webm" ||
        type === "video/quicktime" ||
        type === "audio/mpeg" ||
        type === "audio/aac" ||
        type === "audio/wav" ||
        type === "audio/webm" ||
        type === "application/pdf" ||
        type.startsWith("application/ms") || // microsoft office docs
        type.startsWith("application/vnd.ms") || // microsoft office docs
          type.startsWith("application/vnd.openxml"));
      return supported;
    },

    async send() {
      if (this.limitedFunctionality) {
        bus.$emit("showExpiredSubscription");
      } else {
        if (!this.sending) {
          this.sending = true;
          this.dataUrl = null;
          if (this.file && this.file.size > 0) {
            await this.sendFile().then(() => {
              this.file = null;
              this.sending = false;
              this.scrollToBottom();
            });
          } else if (this.message && this.message.trim().length > 0) {
            await this.sendText().then(() => {
              this.message = "";
              this.sending = false;
              this.scrollToBottom();
            });
          }
        }
      }
    },

    async sendText() {
      const sendingMessage = await createTempMessage(
        this.conversation,
        this.message,
        this.language,
        this.user
      );

      if (this.messages && this.messages.length) {
        sendingMessage.previousMessage = this.messages[
          this.messages.length - 1
        ];
      }

      this.conversation.sendMessage(
        sendingMessage.body,
        sendingMessage.attributes
      );
    },

    async sendFile() {
      let participant;
      this.participants.forEach(p => {
        if (p.identity === this.user.id) {
          participant = p;
        }
      });

      if (!this.file) {
        alert("Please select a file to send.");
      }

      if (!this.supportedFileType(this.file)) {
        alert("File type not supported.");
      }

      if (!this.dataUrl) {
        this.dataUrl = await readFileAsDataURL(this.file);
        if (!this.dataUrl) {
          alert("Unable to read file attachment.");
          return;
        }
      }

      let width;
      let height;
      if (this.file.type.startsWith("image/")) {
        const dimensions = await getImageDimensions(this.dataUrl);
        width = dimensions.width;
        height = dimensions.height;
      }
      const sendingMessage = await createTempMessage(
        this.conversation,
        this.dataUrl,
        this.language,
        this.user,
        this.file.type,
        width,
        height
      );

      if (this.messages && this.messages.length) {
        sendingMessage.previousMessage = this.messages[
          this.messages.length - 1
        ];
      }

      this.messages.push(sendingMessage);

      // send the file async
      return new Promise((resolve, reject) => {
        // generate upload URL
        const fileSize = this.file.size;
        axios
          .post(
            "/rest-api/chat/generate-presigned-upload-url/" +
              this.conversation.attributes.organizationId +
              "/" +
              this.conversation.sid,
            {
              fileName: this.file.name,
              contentType: this.file.type
            }
          )
          .then(response => {
            // will use this later...
            const objectKey = response.data.fileName;
            const contentType = response.data.contentType;

            // configure upload
            const options = {
              onUploadProgress: progressEvent =>
                (sendingMessage.progress = progressEvent.loaded / fileSize),
              headers: {
                "Content-Type": contentType
              }
            };

            // upload file
            axios
              .put(response.data.url, this.file, options)
              .then(uploadResponse => {
                // now send it!
                this.conversation
                  .sendMessage(objectKey, sendingMessage.attributes)
                  .then(() => {
                    resolve();
                  });
              });
          })
          .catch(exc => {
            reject(exc);
          });
      });
    },

    async handleFileChange() {
      this.dataUrl = null;
      if (this.file) {
        if (this.supportedFileType(this.file)) {
          this.dataUrl = await readFileAsDataURL(this.file);
        } else {
          alert("File type not supported.");
          this.file = null;
        }
      } else {
        this.dataUrl = null;
      }
    },

    // user input event handlers -----------
    startTyping() {
      this.conversation.typing();
    },

    // twilio event handlers ---------------
    messageAdded(message) {
      message.addedAfterMount = true; // use this later, it means it was added after component mounted

      // see if this message was sent by us and is a placeholder 'sending' message
      const messageTempId = message.attributes["tempId"];
      if (message.author === this.user.id && messageTempId) {
        // if it is a 'sending' placeholder, then we need to replace a message in the list
        const messageIndex = this.messages.findIndex(
          m => m.sid === messageTempId
        );

        if (messageIndex !== -1) {
          // found it!
          // 1. replace the body with the local data url so we don't have an image flicker
          message.dataUrl = this.messages[messageIndex].body;

          // 2. replace the placeholder message object with the twilio instance
          this.messages.splice(messageIndex, 1, message);
        } else {
          // couldn't find it... this is an error case, but what to do?
          this.messages.push(message);
        }
      } else {
        // otherwise, just add it to the list
        this.messages.push(message);
      }
      this.conversation.setAllMessagesRead();

      // should we translate this message?
      // const language = message.attributes["language"];
      //
      // if (
      //   this.language !== "" &&
      //   language !== null &&
      //   language !== this.language
      // ) {
      //   this.translate(message);
      // }

      this.scrollToBottom();
    },

    messageUpdated(message) {
      // find the message and replace it with the updated one
      const messageIndex = this.messages.findIndex(m => m.sid === message.sid);

      if (messageIndex !== -1) {
        // found it!  now replace it
        this.messages.splice(messageIndex, 1, message);
      }
    },

    messageRemoved(message) {
      // find the message and replace it with the updated one
      const messageIndex = this.messages.findIndex(m => m.sid === message.sid);

      if (messageIndex !== -1) {
        // found it!  now remove it
        this.messages.splice(messageIndex, 1);
      }
    },

    typingStarted(member) {
      // add member to list of people typing
      // but make sure list is only three most recent people
      // and make sure if they are already in the list that we just move them to the front
      const memberIndex = this.typing.findIndex(m => {
        return m.sid === member.sid;
      });

      // add to top of list
      this.typing.unshift(member);

      // if we found them then remove them from the last position
      if (memberIndex !== -1) this.typing.splice(memberIndex, 1);
    },

    typingEnded(member) {
      const memberIndex = this.typing.findIndex(m => m.sid === member.sid);

      if (memberIndex !== -1) this.typing.splice(memberIndex, 1);
    },
    async handleChatRename() {
      await updateChannelFriendlyName(
        this.conversation.sid,
        this.renameChatValue
      );
      this.showRenameChat = false;
    },
    async handleEnter(e) {
      if (e.shiftKey) {
        this.message += "\n";
      } else {
        await this.send();
      }
    }
  },
  computed: {
    isImageAttachment() {
      if (!this.file) return false;
      return this.file.type.startsWith("image/");
    },
    isVideoAttachment() {
      if (!this.file) return false;
      return this.file.type.startsWith("video/");
    },
    isFileAttachment() {
      return (
        this.file && !this.isImageAttachment() && !this.isVideoAttachment()
      );
    },
    zoomMediaUrl() {
      return mediaUrl(this.mediaMessage, true);
    },
    showZoomImage() {
      return isImage(this.mediaMessage);
    },
    attachmentIcon() {
      if (this.isImage) {
        return "mdi-insert-photo";
      } else if (this.isVideo) {
        return "mdi-smart-display";
      } else {
        return "mdi-file-present";
      }
    },
    friendlyFileName() {
      let fileName = this.file && this.file.name;
      if (fileName) {
        const pos = fileName.indexOf("_");
        fileName = pos === -1 ? fileName : fileName.substring(pos + 1);
      }
      return fileName;
    },
    visibleParticipants() {
      if (this.showAllParticipants) {
        return this.participants;
      } else {
        return this.participants.length > 10
          ? this.participants.slice(0, 10)
          : this.participants;
      }
    },
    hiddenParticipants() {
      if (this.showAllParticipants) {
        return [];
      } else {
        return this.participants.length > 10 ? this.participants.slice(10) : [];
      }
    },
    reactActionLabel() {
      if (this.reactionContext) {
        const message = this.reactionContext.message;
        return message.isImage ? "View" : message.isVideo ? "View" : null;
      } else {
        return null;
      }
    },
    reactMenuPosition() {
      if (this.reactionContext) {
        const event = this.reactionContext.event;
        const offsetWidth = 250;
        const pos = { left: event.pageX - offsetWidth, top: event.pageY };
        return pos;
      } else {
        return { left: 0, top: 0 };
      }
    }
  }
});
</script>
<style scoped>
.chat-body {
  height: calc(
    100vh - (88px + 75px + 115px)
  ); /* viewport-height - (top bar + chat-header + chat-footer) */
  overflow: hidden;
}

.chat-header {
  overflow: hidden;
  min-height: 90px;
  margin-bottom: 5px;
  border-bottom: 1px solid #ddd;
}

.chat-footer {
  height: 75px;
  padding: 15px 10px;
}

.conversation {
  display: flex;
  flex-direction: column;
  color: black;
  height: 100%;
  align-items: stretch;
}

.conversation-header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  justify-items: stretch;
  color: black;
  padding: 5px 20px 15px 20px;
  height: 100%;
  align-items: stretch;
}
.back-button {
  height: auto;
}
.v-input__icon--append .v-icon {
  color: green;
}
.v-input__icon--append-outer .v-icon {
  color: red;
}

@media (min-width: 960px) {
  .conversation-header {
    padding: 20px;
  }

  .back-button {
    display: none;
  }
}

.messages-layout {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  justify-content: space-between;
  max-height: 100%;
}

.messages-container {
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  overflow-y: auto;
  flex-grow: 1;
  padding-left: 15px;
  padding-right: 15px;
}

.send-message {
  padding-right: 20px;
  /*position: absolute;*/
  /*  bottom: 20px;*/
  /*  left: 15px;*/
  /*right: 15px;*/
}

.send-message-file-input {
  height: 0;
  z-index: 1000;
  margin-left: 34px;
  background-color: #f9f9f9;
  box-shadow: 0px 3px 1px -2px rgb(0 0 0 / 20%),
    0px 2px 2px 0px rgb(0 0 0 / 14%), 0px 1px 5px 0px rgb(0 0 0 / 12%);
}

.image-delete {
  position: absolute;
  top: 4px;
  right: 4px;
}

.file-attachment {
  margin-top: -45px;
  margin-left: -2px;
  max-width: 33%;
  min-width: 200px;
  height: 40px;
  border-radius: 5px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  padding: 10px 5px 5px 10px;
  font-size: 11px;
  color: black;
  background-color: rgba(255, 255, 255, 0.85);
  z-index: 1000;
}
.file-attachment:hover {
  color: #666;
  cursor: pointer;
}
.file-attachment:hover span {
  color: darkred;
  text-decoration: line-through;
}
.file-attachment:hover .v-icon {
  color: darkred;
}

.file-attachment .v-icon {
  margin-top: -2px;
  margin-right: 10px;
  color: black;
}

.black-icon-background {
  position: absolute;
  top: 7px;
  right: 7px;
  background-color: black;
  width: 18px;
  border-radius: 50%;
  height: 18px;
  box-shadow: 1px 1px 5px black;
}

.message-input .v-icon {
  color: dodgerblue !important;
}

/deep/ .mdi-camera {
}

.unread-indicator .v-icon {
  color: transparent;
  float: right;
}

.has-unread .unread-indicator .v-icon {
  color: red;
}

.v-dialog__content {
  --background-color: black;
}

.members-typing {
  color: red;
  text-align: center;
}

/*.members .member:after,*/
/*.members-typing .member:after {*/
/*  content: ", ";*/
/*}*/

.members .member:last-child:after,
.members-typing .member:last-child:after,
.members .member:nth-last-child(2):after,
.members-typing .member:nth-last-child(2):after {
  content: "" !important;
}

/*.members .member:last-child:before,*/
/*.members-typing .member:nth-child(n + 2):last-child:before {*/
/*  content: " and ";*/
/*}*/

.members .member:first-child:before,
.members-typing .member:first-child:before {
  content: "" !important;
}

.tb-red-bg {
  background-color: var(--tb-red) !important;
  color: white;
}

.member-layout {
  background: white;
}
</style>
<style>
.send-message textarea {
  max-height: 300px !important;
  overflow: scroll !important;
}
.message-input::-webkit-scrollbar {
  width: 0 !important;
}
</style>
